import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { reportsApi, historicalApi } from '@/lib/api';
import axios from 'axios';
import { ArrowLeft, Download, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Target, Zap, Info, ChevronDown, ChevronUp, Package, Users, BarChart3 } from 'lucide-react';
import { PositioningChart, RadarPositionChart, LeversChart } from '@/components/Charts';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cdfce993-612e-4617-ac7b-89da32b19bd7/artifacts/wcwsdulx_Logo%20benchory.png";
const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v, type) => {
  if (v === undefined || v === null) return '—';
  if (type === 'currency') return `${Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`;
  if (type === 'percentage') return `${Number(v).toFixed(1)} %`;
  if (type === 'decimal') return Number(v).toFixed(2);
  return Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 1 });
};

const MONTHS_FR = {
  "01": "Jan.", "02": "Fév.", "03": "Mars", "04": "Avr.",
  "05": "Mai", "06": "Juin", "07": "Juil.", "08": "Août",
  "09": "Sep.", "10": "Oct.", "11": "Nov.", "12": "Déc.",
};

function ScoreGauge({ score }) {
  const color = score >= 70 ? '#10B981' : score >= 45 ? '#B8860B' : '#EF4444';
  const pct = Math.min(100, Math.max(0, score));
  return (
    <div className="flex flex-col items-center" data-testid="score-gauge">
      <div className="relative w-28 h-28">
        <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
          <circle cx="60" cy="60" r="50" fill="none" stroke="#E5E7EB" strokeWidth="10" />
          <circle cx="60" cy="60" r="50" fill="none" stroke={color} strokeWidth="10"
            strokeDasharray={`${pct * 3.14} ${314 - pct * 3.14}`} strokeLinecap="round"
            className="transition-all duration-1000" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold text-[#1B2A4A] font-manrope">{score}</span>
          <span className="text-[10px] text-gray-400 font-medium">/100</span>
        </div>
      </div>
      <p className="text-xs text-gray-500 mt-2 font-medium">Score stratégique</p>
    </div>
  );
}

function PositionSlider({ percentile }) {
  const pos = Math.min(98, Math.max(2, percentile));
  const color = pos >= 60 ? '#10B981' : pos >= 40 ? '#B8860B' : '#EF4444';
  return (
    <div className="relative h-7 mt-2" data-testid="position-slider">
      <div className="absolute top-2 left-0 right-0 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-red-300 via-yellow-200 to-emerald-300 w-full opacity-60" />
      </div>
      {[0, 25, 50, 75, 100].map((p, i) => (
        <div key={i} className="absolute top-1" style={{ left: `${p}%`, transform: 'translateX(-50%)' }}>
          <div className="w-px h-4 bg-gray-300" />
        </div>
      ))}
      <div className="absolute top-0" style={{ left: `${pos}%`, transform: 'translateX(-50%)' }}>
        <div className="w-4 h-4 rounded-full border-2 border-white shadow-md" style={{ backgroundColor: color }} />
      </div>
      <div className="absolute top-5 left-0 right-0 flex justify-between text-[9px] text-gray-400 font-medium">
        <span>Bas</span><span>Q1</span><span>Méd.</span><span>Q3</span><span>Haut</span>
      </div>
    </div>
  );
}

function KPICard({ kpi }) {
  const [showMethodo, setShowMethodo] = useState(false);
  const isGood = kpi.gap_euros >= 0;
  const hasEuroImpact = kpi.gap_euros !== 0 && kpi.euro_detail && !kpi.euro_detail.startsWith('Indicateur');

  return (
    <div className={`bg-white border rounded-lg p-5 ${kpi.needs_action ? 'border-amber-200 bg-amber-50/20' : 'border-gray-100'}`} data-testid={`kpi-card-${kpi.key}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <h4 className="font-bold text-[#1B2A4A] text-sm font-manrope">{kpi.label}</h4>
          <p className="text-xs text-gray-400 mt-0.5">{kpi.position_label}</p>
        </div>
        <div className="flex items-center gap-1">
          {kpi.is_primary_lever && <span className="px-1.5 py-0.5 bg-[#1B2A4A]/5 text-[#1B2A4A] text-[9px] font-bold rounded uppercase">Levier</span>}
          {kpi.needs_action && <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[9px] font-bold rounded uppercase">Action recommandée</span>}
        </div>
      </div>
      <div className="flex items-end gap-3 mb-3">
        <span className="text-2xl font-bold text-[#1B2A4A] font-manrope">{fmt(kpi.value, kpi.format_type)}</span>
        <span className="text-xs text-gray-400 pb-1">vs médiane : {fmt(kpi.cohort_median, kpi.format_type)}</span>
      </div>
      <PositionSlider percentile={kpi.percentile} />
      <div className="mt-4 pt-3 border-t border-gray-100 grid grid-cols-5 gap-1 text-center">
        {[
          { l: 'Bas', v: kpi.cohort_min }, { l: 'Q1', v: kpi.cohort_q1 },
          { l: 'Méd.', v: kpi.cohort_median }, { l: 'Moy.', v: kpi.cohort_mean },
          { l: 'Haut', v: kpi.cohort_max },
        ].map(s => (
          <div key={s.l}>
            <p className="text-[9px] text-gray-400 font-medium">{s.l}</p>
            <p className="text-xs font-semibold text-[#1B2A4A]">{fmt(s.v, kpi.format_type)}</p>
          </div>
        ))}
      </div>
      {/* Explanation */}
      {kpi.explanation && (
        <div className="mt-3 px-3 py-2 rounded-lg bg-[#1B2A4A]/[0.03] border border-[#1B2A4A]/5">
          <p className="text-[11px] text-gray-600 leading-relaxed">{kpi.explanation}</p>
        </div>
      )}
      {hasEuroImpact && (
        <div className={`mt-3 px-3 py-2 rounded-lg text-xs font-medium ${isGood ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
          <div className="flex items-center gap-2">
            {isGood ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            <span>Simulation : {isGood ? '+' : ''}{Number(kpi.gap_euros).toLocaleString('fr-FR')} € vs médiane</span>
          </div>
          <p className="text-[10px] mt-1 opacity-70">{kpi.euro_detail}</p>
        </div>
      )}
      {!hasEuroImpact && kpi.euro_detail && (
        <div className="mt-3 px-3 py-2 rounded-lg text-[10px] text-gray-400 bg-gray-50 italic">{kpi.euro_detail}</div>
      )}
      {/* Targeted action tip — only for 4 worst */}
      {kpi.action_tip && kpi.needs_action && (
        <div className="mt-3 px-3 py-2.5 rounded-lg bg-[#B8860B]/5 border border-[#B8860B]/10">
          <div className="flex items-center gap-1.5 mb-1">
            <Zap size={12} className="text-[#B8860B]" />
            <span className="text-[10px] font-bold text-[#B8860B] uppercase tracking-wider">Piste d'amélioration</span>
          </div>
          <p className="text-[11px] text-gray-700 leading-relaxed">{kpi.action_tip}</p>
        </div>
      )}
      {kpi.methodology && (
        <button onClick={() => setShowMethodo(!showMethodo)} className="mt-2 flex items-center gap-1 text-[10px] text-gray-400 hover:text-gray-600 transition-colors">
          <Info size={10} /> Formule de calcul
          {showMethodo ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
        </button>
      )}
      {showMethodo && <p className="text-[10px] text-gray-400 mt-1 px-2 py-1 bg-gray-50 rounded italic">{kpi.methodology}</p>}
    </div>
  );
}

function HistoricalSection({ estId }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!estId) return;
    historicalApi.get(estId).then(r => {
      setHistory(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [estId]);

  if (loading || !history || history.history.length < 2) return null;

  const data = history.history;
  const maxCa = Math.max(...data.map(d => Math.max(d.ca_ht, d.market_ca_avg)));
  const maxCl = Math.max(...data.map(d => Math.max(d.nb_clients, d.market_clients_avg)));

  return (
    <div className="px-10 pb-10 print-break" data-testid="historical-section">
      <div className="flex items-center gap-2 mb-6 pt-6 border-t border-gray-100">
        <div className="w-1 h-6 bg-[#B8860B] rounded-full" />
        <h2 className="text-xl font-bold text-[#1B2A4A] font-manrope">Historique &mdash; Vue 12 mois</h2>
      </div>

      {/* Table */}
      <div className="overflow-x-auto mb-8">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b-2 border-gray-200">
              <th className="text-left py-2 px-2 font-bold text-gray-500 uppercase tracking-wider text-[10px]">Mois</th>
              <th className="text-right py-2 px-2 font-bold text-gray-500 uppercase tracking-wider text-[10px]">CA HT</th>
              <th className="text-right py-2 px-2 font-bold text-gray-500 uppercase tracking-wider text-[10px]">Clients</th>
              <th className="text-right py-2 px-2 font-bold text-gray-500 uppercase tracking-wider text-[10px]">Panier</th>
              <th className="text-right py-2 px-2 font-bold text-gray-500 uppercase tracking-wider text-[10px]">Matières</th>
              <th className="text-right py-2 px-2 font-bold text-gray-500 uppercase tracking-wider text-[10px]">Personnel</th>
              <th className="text-right py-2 px-2 font-bold text-gray-500 uppercase tracking-wider text-[10px]">Résultat</th>
            </tr>
          </thead>
          <tbody>
            {data.map(h => {
              const [y, m] = h.period.split('-');
              return (
                <tr key={h.period} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="py-2 px-2 font-medium text-[#1B2A4A]">{MONTHS_FR[m]} {y}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{Number(h.ca_ht).toLocaleString('fr-FR')} €</td>
                  <td className="py-2 px-2 text-right tabular-nums">{Number(h.nb_clients).toLocaleString('fr-FR')}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{h.panier.toFixed(1)} €</td>
                  <td className="py-2 px-2 text-right tabular-nums">{h.matieres_pct.toFixed(1)}%</td>
                  <td className="py-2 px-2 text-right tabular-nums">{h.personnel_pct.toFixed(1)}%</td>
                  <td className={`py-2 px-2 text-right tabular-nums font-bold ${h.resultat_pct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {h.resultat_pct.toFixed(1)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <MiniChart
          title="Clients servis / mois"
          data={data}
          valueKey="nb_clients"
          marketKey="market_clients_avg"
          maxVal={maxCl}
        />
        <MiniChart
          title="Chiffre d'affaires HT / mois"
          data={data}
          valueKey="ca_ht"
          marketKey="market_ca_avg"
          maxVal={maxCa}
          isCurrency
        />
      </div>

      <div className="mt-6 bg-gray-50 rounded-lg p-4 border border-gray-100">
        <p className="text-xs font-bold text-[#1B2A4A] mb-1">Tendances et décision</p>
        <p className="text-[11px] text-gray-500">Repérez vos 2 meilleurs mois et 2 plus faibles. Décidez : planning, stocks et sécurisation fournisseurs en période haute ; réduction d'amplitude et offres adaptées en période basse.</p>
      </div>
    </div>
  );
}

function MiniChart({ title, data, valueKey, marketKey, maxVal, isCurrency = false }) {
  const w = 400, h = 140, mx = 10, my = 20;
  const usable_w = w - 2 * mx;
  const usable_h = h - 2 * my;

  const toX = (i) => mx + (i / Math.max(1, data.length - 1)) * usable_w;
  const toY = (v) => h - my - (v / maxVal) * usable_h;

  const estPath = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(d[valueKey]).toFixed(1)}`).join(' ');
  const mktPath = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(d[marketKey]).toFixed(1)}`).join(' ');

  return (
    <div className="bg-white border border-gray-100 rounded-lg p-4">
      <p className="text-xs font-bold text-[#1B2A4A] mb-3">{title}</p>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ height: '120px' }}>
        {/* Grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((f, i) => (
          <line key={i} x1={mx} y1={h - my - f * usable_h} x2={w - mx} y2={h - my - f * usable_h} stroke="#f0f0f0" strokeWidth="0.5" />
        ))}
        {/* Market line */}
        <path d={mktPath} fill="none" stroke="#B8860B" strokeWidth="1.5" strokeDasharray="4,2" opacity="0.7" />
        {/* Establishment line */}
        <path d={estPath} fill="none" stroke="#1B2A4A" strokeWidth="2" />
        {/* X labels */}
        {data.map((d, i) => {
          const [, m] = d.period.split('-');
          return <text key={i} x={toX(i)} y={h - 2} textAnchor="middle" fontSize="7" fill="#9CA3AF">{MONTHS_FR[m]}</text>;
        })}
      </svg>
      <div className="flex gap-4 mt-2 text-[10px]">
        <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-[#1B2A4A] inline-block"></span> Votre établissement</span>
        <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-[#B8860B] inline-block" style={{ borderTop: '1px dashed #B8860B' }}></span> Marché (moyenne)</span>
      </div>
    </div>
  );
}

export default function ReportViewerPage({ isClientView = false }) {
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('benchory_token');
    if (isClientView) {
      axios.get(`${API_URL}/client/reports/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => { setReport(r.data); setLoading(false); }).catch(() => setLoading(false));
    } else {
      reportsApi.get(reportId).then(r => { setReport(r.data); setLoading(false); }).catch(() => setLoading(false));
    }
  }, [reportId, isClientView]);

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await reportsApi.downloadPdf(report.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `BENCHORY_${report.establishment_name.replace(/ /g, '_')}_${report.period}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download error:', err);
    }
    setDownloading(false);
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-3 border-[#1B2A4A]/20 border-t-[#1B2A4A] rounded-full animate-spin" /></div>;
  if (!report) return <div className="text-center py-20 text-gray-500">Rapport non trouvé</div>;

  const { diagnostic, kpi_results, modules, piloting_modules, establishment_profile, monthly_raw } = report;
  const getModuleKpis = (moduleKey) => kpi_results.filter(k => k.module === moduleKey);
  const backPath = isClientView ? '/espace-client' : '/reports';

  return (
    <div className="pb-12" data-testid="report-viewer-page">
      <div className="no-print flex items-center justify-between mb-6">
        <Link to={backPath} className="flex items-center gap-2 text-gray-500 hover:text-[#1B2A4A] text-sm font-medium transition-colors" data-testid="report-back-btn">
          <ArrowLeft size={16} /> Retour
        </Link>
        <button
          onClick={handleDownloadPdf}
          disabled={downloading}
          className="flex items-center gap-2 bg-[#1B2A4A] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#152039] shadow-md transition-all disabled:opacity-50"
          data-testid="report-pdf-btn"
        >
          {downloading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Download size={16} />}
          {downloading ? 'Génération...' : 'Télécharger PDF'}
        </button>
      </div>

      <div className="report-page max-w-4xl mx-auto bg-white shadow-xl border border-gray-100 rounded-lg overflow-hidden">
        {/* COVER */}
        <div className="bg-[#1B2A4A] text-white p-10 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 opacity-5">
            <div className="absolute top-8 right-8 w-48 h-48 rounded-full border-2 border-white" />
          </div>
          <div className="relative z-10 flex items-start justify-between">
            <div>
              <img src={LOGO_URL} alt="BENCHORY" className="h-14 mb-6" />
              <p className="text-white/50 text-xs uppercase tracking-[0.2em] mb-2">Rapport de pilotage</p>
              <h1 className="text-3xl font-bold font-playfair tracking-wide">{report.establishment_name}</h1>
              <p className="text-white/70 mt-2 text-sm">{report.period_label}</p>
              <p className="text-white/40 text-xs mt-1">{report.segment_label}</p>
            </div>
            <div className="text-right">
              <p className="text-white/40 text-xs">Cohorte</p>
              <p className="text-2xl font-bold text-[#B8860B]">{report.sample_size}</p>
              <p className="text-white/40 text-xs">établissements</p>
            </div>
          </div>
          <div className="mt-6 pt-4 border-t border-white/10 text-xs text-white/30">
            Document confidentiel — Usage exclusif de l'établissement destinataire
          </div>
        </div>

        {/* ESTABLISHMENT PROFILE + RAW DATA */}
        {(establishment_profile || monthly_raw) && (
          <div className="px-10 pt-8 pb-6" data-testid="profile-section">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {establishment_profile && (
                <div>
                  <p className="text-[10px] font-bold text-[#B8860B] uppercase tracking-wider mb-3">Profil établissement</p>
                  <div className="space-y-1.5">
                    {[
                      ['Zone', establishment_profile.zone],
                      ['Secteur', establishment_profile.sector],
                      ['Taille', establishment_profile.size_category],
                      ['Places assises', establishment_profile.capacity || '—'],
                      ['Surface', `${establishment_profile.surface_m2 || 0} m²`],
                      ['Heures/semaine', `${establishment_profile.opening_hours_week || 0}h`],
                      ['Effectif (ETP)', establishment_profile.employees_fte || '—'],
                    ].map(([label, val]) => (
                      <div key={label} className="flex justify-between text-xs border-b border-gray-50 pb-1">
                        <span className="text-gray-500">{label}</span>
                        <span className="font-semibold text-[#1B2A4A]">{val}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {monthly_raw && (
                <div>
                  <p className="text-[10px] font-bold text-[#B8860B] uppercase tracking-wider mb-3">Données du mois <span className="text-gray-400 font-normal">(tous montants HT)</span></p>
                  <div className="space-y-1.5">
                    {(monthly_raw.achats_marchandises > 0 ? [
                      ['CA HT total', `${Number(monthly_raw.ca_ht).toLocaleString('fr-FR')} €`],
                      ['Achats HT', `${Number(monthly_raw.achats_marchandises).toLocaleString('fr-FR')} €`],
                      ['Coût personnel', `${Number(monthly_raw.cout_personnel).toLocaleString('fr-FR')} €`],
                      ['Charges externes', `${Number(monthly_raw.charges_externes || 0).toLocaleString('fr-FR')} €`],
                      ['CA Parapharmacie', `${Number(monthly_raw.ca_parapharmacie || 0).toLocaleString('fr-FR')} €`],
                      ['Ordonnances', Number(monthly_raw.nb_ordonnances || 0).toLocaleString('fr-FR')],
                      ['Jours ouverts', monthly_raw.jours_ouverture],
                      ['Stock moyen', `${Number(monthly_raw.stock_moyen).toLocaleString('fr-FR')} €`],
                      ['Trésorerie dispo.', `${Number(monthly_raw.tresorerie_disponible || 0).toLocaleString('fr-FR')} €`],
                    ] : [
                      ['CA HT', `${Number(monthly_raw.ca_ht).toLocaleString('fr-FR')} €`],
                      ['Clients', Number(monthly_raw.nb_clients).toLocaleString('fr-FR')],
                      ['Jours ouverts', monthly_raw.jours_ouverture],
                      ['Coût matières', `${Number(monthly_raw.cout_matieres).toLocaleString('fr-FR')} €`],
                      ['Coût personnel', `${Number(monthly_raw.cout_personnel).toLocaleString('fr-FR')} €`],
                      ['Stock moyen', `${Number(monthly_raw.stock_moyen).toLocaleString('fr-FR')} €`],
                      ['Coût énergie', `${Number(monthly_raw.cout_energie).toLocaleString('fr-FR')} €`],
                      ['Impôts & taxes', `${Number(monthly_raw.impots_taxes).toLocaleString('fr-FR')} €`],
                    ]).map(([label, val]) => (
                      <div key={label} className="flex justify-between text-xs border-b border-gray-50 pb-1">
                        <span className="text-gray-500">{label}</span>
                        <span className="font-semibold text-[#1B2A4A]">{val}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* DIAGNOSTIC EXPRESS */}
        <div className="p-10" data-testid="diagnostic-express">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-1 h-6 bg-[#B8860B] rounded-full" />
            <h2 className="text-xl font-bold text-[#1B2A4A] font-manrope">Synthèse dirigeant — Diagnostic express</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="flex flex-col items-center justify-center gap-4">
              <ScoreGauge score={diagnostic.score_global} />
              {diagnostic.raw_score !== undefined && (
                <div className="text-center" data-testid="raw-score-display">
                  <p className="text-xl font-bold text-gray-500">{diagnostic.raw_score}</p>
                  <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Positionnement marché</p>
                </div>
              )}
            </div>
            <div>
              <h3 className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <CheckCircle size={12} /> Forces
              </h3>
              <ul className="space-y-2">
                {diagnostic.forces.map((f, i) => (
                  <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />{f}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-xs font-semibold text-red-600 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <AlertTriangle size={12} /> Risques
              </h3>
              <ul className="space-y-2">
                {diagnostic.risques.map((r, i) => (
                  <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 shrink-0" />{r}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="bg-[#1B2A4A]/[0.03] rounded-lg p-5 mb-6 border border-[#1B2A4A]/5">
            <div className="flex items-center gap-2 mb-2">
              <Target size={16} className="text-[#B8860B]" />
              <h3 className="text-sm font-bold text-[#1B2A4A] uppercase tracking-wide">Levier prioritaire du mois</h3>
            </div>
            <p className="text-sm text-gray-700">{diagnostic.levier_prioritaire}</p>
          </div>

          {/* Cost of inaction */}
          <div className="bg-red-50 rounded-lg p-5 mb-6 border border-red-100" data-testid="cout-inaction-section">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-xs font-semibold text-red-600 uppercase tracking-wider mb-1">Coût estimé de l'inaction</p>
                <p className="text-sm text-red-700/70">Somme des écarts négatifs sur les leviers primaires (sans double-comptage)</p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-red-600 font-manrope" data-testid="cout-inaction">
                  {Number(diagnostic.cout_inaction).toLocaleString('fr-FR')} €
                </p>
                <p className="text-xs text-red-500">/mois</p>
                {diagnostic.cout_inaction_annuel > 0 && (
                  <p className="text-lg font-bold text-red-800 mt-1" data-testid="cout-inaction-annuel">
                    soit {Number(diagnostic.cout_inaction_annuel).toLocaleString('fr-FR')} € /an
                  </p>
                )}
              </div>
            </div>
            {diagnostic.inaction_levers?.length > 0 && (
              <div className="pt-3 border-t border-red-100 space-y-1.5">
                <p className="text-[10px] font-semibold text-red-600 uppercase tracking-wider">Détail des leviers :</p>
                {diagnostic.inaction_levers.map((l, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="text-red-700/70">{l.label}</span>
                    <span className="font-bold text-red-600">{Number(l.euros).toLocaleString('fr-FR')} €</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 30-day plan */}
          <div className="bg-[#B8860B]/5 rounded-lg p-5 border border-[#B8860B]/10">
            <div className="flex items-center gap-2 mb-3">
              <Zap size={16} className="text-[#B8860B]" />
              <h3 className="text-sm font-bold text-[#1B2A4A] uppercase tracking-wide">Plan 30 jours</h3>
            </div>
            <div className="space-y-2 mb-3">
              {diagnostic.actions_30j.map((a, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-[#B8860B] text-white text-xs flex items-center justify-center shrink-0 mt-0.5 font-bold">{i + 1}</span>
                  <p className="text-sm text-gray-700">{a}</p>
                </div>
              ))}
            </div>
            {diagnostic.mesure && (
              <div className="pt-3 border-t border-[#B8860B]/10">
                <p className="text-xs text-gray-500"><span className="font-semibold text-[#1B2A4A]">Mesure :</span> {diagnostic.mesure}</p>
              </div>
            )}
          </div>

          {report.ai_text && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-100">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Commentaire</p>
              <p className="text-sm text-gray-700 leading-relaxed">{report.ai_text}</p>
            </div>
          )}
        </div>

        {/* PILOTING MODULES */}
        {piloting_modules?.length > 0 && (
          <div className="px-10 pb-8" data-testid="piloting-modules">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-6 bg-[#1B2A4A] rounded-full" />
              <h2 className="text-lg font-bold text-[#1B2A4A] font-manrope">Modules de pilotage</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {piloting_modules.map((pm, i) => {
                const icons = { 'alert-triangle': AlertTriangle, 'trending-down': TrendingDown, 'trending-up': TrendingUp, 'users': Users, 'package': Package, 'activity': BarChart3 };
                const Icon = icons[pm.icon] || BarChart3;
                return (
                  <div key={i} className="bg-[#F8F9FA] rounded-lg p-5 border border-gray-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon size={16} className="text-[#1B2A4A]" />
                      <h4 className="font-bold text-[#1B2A4A] text-sm font-manrope">{pm.title}</h4>
                      <span className="text-xs text-gray-400">{pm.subtitle}</span>
                    </div>
                    <p className="text-xs text-gray-600 mb-2">{pm.description}</p>
                    <p className="text-xs text-[#B8860B] font-medium"><span className="font-bold">Action 30j :</span> {pm.action_30j}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* MODULES (KPI Details) */}
        {Object.entries(modules).map(([moduleKey, mod]) => {
          const moduleKpis = getModuleKpis(moduleKey);
          if (moduleKpis.length === 0) return null;
          return (
            <div key={moduleKey} className="px-10 pb-10 print-break" data-testid={`module-${moduleKey}`}>
              <div className="flex items-center gap-2 mb-6 pt-6 border-t border-gray-100">
                <div className="w-1 h-6 bg-[#1B2A4A] rounded-full" />
                <div>
                  <h2 className="text-xl font-bold text-[#1B2A4A] font-manrope">{mod.title}</h2>
                  <p className="text-xs text-gray-400">{mod.description}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {moduleKpis.map(kpi => <KPICard key={kpi.key} kpi={kpi} />)}
              </div>
            </div>
          );
        })}

        {/* VISUAL CHARTS */}
        {kpi_results.length > 0 && (
          <div className="px-10 pb-10 print-break" data-testid="report-charts">
            <div className="flex items-center gap-2 mb-6 pt-6 border-t border-gray-100">
              <div className="w-1 h-6 bg-[#B8860B] rounded-full" />
              <h2 className="text-xl font-bold text-[#1B2A4A] font-manrope">Analyse graphique</h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h3 className="text-xs font-bold text-[#B8860B] uppercase tracking-wider mb-3">Positionnement face au marché</h3>
                <p className="text-[10px] text-gray-400 mb-2">Vos principaux indicateurs comparés à la médiane du secteur</p>
                <PositioningChart data={kpi_results.map(k => ({ label: k.label, score: k.percentile || 50 }))} />
              </div>
              <div>
                <h3 className="text-xs font-bold text-[#B8860B] uppercase tracking-wider mb-3">Profil radar</h3>
                <p className="text-[10px] text-gray-400 mb-2">Vue synthétique de vos zones d'attention</p>
                <RadarPositionChart data={kpi_results.map(k => ({ label: k.label, score: k.percentile || 50 }))} />
              </div>
              {diagnostic.inaction_levers?.length > 0 && (
                <div className="lg:col-span-2">
                  <h3 className="text-xs font-bold text-red-600 uppercase tracking-wider mb-3">Leviers d'amélioration — Impact en euros</h3>
                  <p className="text-[10px] text-gray-400 mb-2">Les postes où chaque euro récupéré aura le plus d'impact</p>
                  <LeversChart data={diagnostic.inaction_levers} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* RECAP */}
        <div className="px-10 pb-10 print-break" data-testid="report-recap">
          <div className="flex items-center gap-2 mb-6 pt-6 border-t border-gray-100">
            <div className="w-1 h-6 bg-[#B8860B] rounded-full" />
            <h2 className="text-xl font-bold text-[#1B2A4A] font-manrope">Récapitulatif — Performance face au marché</h2>
          </div>
          <div className="space-y-3">
            {kpi_results.map(kpi => {
              const pct = Math.min(100, Math.max(0, kpi.percentile));
              const barColor = pct >= 60 ? '#10B981' : pct >= 40 ? '#B8860B' : '#EF4444';
              return (
                <div key={kpi.key} className="flex items-center gap-4">
                  <div className="w-52 text-xs text-gray-600 text-right font-medium truncate flex items-center justify-end gap-1">
                    {kpi.is_primary_lever && <span className="w-1.5 h-1.5 rounded-full bg-[#B8860B]" />}
                    {kpi.label}
                  </div>
                  <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden relative">
                    <div className="absolute inset-y-0 left-0 rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: barColor }} />
                    <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-300" />
                  </div>
                  <div className="w-12 text-right text-xs font-bold" style={{ color: barColor }}>{Math.round(pct)}</div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-6 text-xs text-gray-400">
            <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-full bg-red-500" /> Attention (&lt;40)</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-full bg-[#B8860B]" /> Correct (40-60)</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-full bg-emerald-500" /> Excellent (&gt;60)</span>
            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#B8860B]" /> Levier primaire</span>
          </div>
        </div>

        {/* HISTORICAL SECTION */}
        <HistoricalSection estId={report.establishment_id} />

        {/* METHODOLOGY */}
        <div className="px-10 pb-8 print-break">
          <div className="bg-gray-50 rounded-lg p-5 border border-gray-100">
            <h3 className="text-xs font-bold text-[#1B2A4A] uppercase tracking-wider mb-2">Note méthodologique</h3>
            <p className="text-[11px] text-gray-500 leading-relaxed">
              <strong>Score global :</strong> Moyenne des percentiles sur les {kpi_results.length} indicateurs. 50 = niveau médian de la cohorte.
            </p>
            <p className="text-[11px] text-gray-500 leading-relaxed mt-2">
              <strong>Coût de l'inaction :</strong> Somme des écarts négatifs uniquement sur les leviers primaires. Les indicateurs dérivés ne sont pas comptabilisés pour éviter le double-comptage.
            </p>
            <p className="text-[11px] text-gray-500 leading-relaxed mt-2">
              <strong>Cohorte :</strong> {report.sample_size} établissements comparables ({report.segment_label}).
            </p>
          </div>
        </div>

        <div className="bg-[#1B2A4A] text-white/40 px-10 py-4 text-xs flex items-center justify-between">
          <span>BENCHORY — La transparence crée le progrès</span>
          <span>Document confidentiel — {report.period_label}</span>
        </div>
      </div>
    </div>
  );
}
